import React from 'react';

import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import TextType from './TextType';

export const Hero: React.FC = () => {
  return (
    <motion.section 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 2.2 }}
      className="relative overflow-hidden min-h-[748px] h-auto flex items-center pt-24 pb-12 lg:py-0" 
      style={{ minHeight: '748px' }}
    >
      {/* Background Image */}
      <motion.div 
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.5, delay: 2.4 }}
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/Images/hero_pa.webp)' }}
      />
      
      {/* Floating Particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-primary/20 backdrop-blur-sm"
          initial={{ 
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000), 
            y: Math.random() * 748,
            scale: 0.5,
            opacity: 0
          }}
          animate={{ 
            y: [null, Math.random() * -100],
            scale: [0.5, 1, 0.5],
            opacity: [0, 0.5, 0]
          }}
          transition={{
            duration: 5 + Math.random() * 5,
            repeat: Infinity,
            ease: "linear",
            delay: Math.random() * 2
          }}
          style={{
            width: Math.random() * 20 + 10 + 'px',
            height: Math.random() * 20 + 10 + 'px',
          }}
        />
      ))}

      <div className="container mx-auto px-4 relative z-10 h-full flex items-center">
        <div className="grid grid-cols-12 gap-8 items-center w-full">
          {/* Left Column - Text Content */}
          <div className="col-span-12 lg:col-span-4 order-1 lg:order-1 relative z-20 mt-8 lg:mt-0">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 2.6 }}
              className="text-left"
            >
              <h1 
                className="mb-64 md:mb-8 leading-tight"
                style={{
                  fontFamily: 'Nanum Myeongjo',
                  fontWeight: 800,
                  fontSize: '48px',
                  lineHeight: '58px',
                  letterSpacing: '0%'
                }}
              >
                <TextType
                  baseOpacity={0}
                  enableBlur={true}
                  baseRotation={5}
                  blurStrength={10}
                  size="lg"
                  duration={1}
                  textClassName="text-[30px] md:text-[44px] leading-[36px] md:leading-[58px] font-extrabold text-[#620131]"
                >
                  Premium Print.
                </TextType>
                <TextType
                  baseOpacity={0}
                  enableBlur={true}
                  baseRotation={5}
                  blurStrength={10}
                  size="lg"
                  duration={1}
                  textClassName="text-[25px] md:text-[44px] leading-[36px] md:leading-[58px] font-extrabold text-[#620131]"
                >
                  Precision Packaging.
                </TextType>
              </h1>
              
              <div className="flex items-center gap-3 mb-8">
                <img 
                  src="/Images/hero_client.webp" 
                  alt="Happy Clients" 
                  className="h-12 object-contain"
                />
                <div className="flex flex-col">
                  <div className="flex text-orange-400 text-xs mb-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} size={12} fill="currentColor" />
                    ))}
                  </div>
                  <span className="text-xs font-bold">2500+ Happy Clients</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Center Column - Product Box */}
          <div className="col-span-12 lg:col-span-4 flex items-center justify-center order-2 lg:order-2 relative z-10 mt-8 lg:mt-0">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="w-full flex items-center justify-center"
            >
              {/* <img 
                src="/Images/Packaging Box.webp" 
                alt="PrintAura Product Box" 
                className="opacity-0 w-full object-contain transform hover:scale-105 transition-transform duration-500"
                style={{ maxWidth: '550px' }}
              /> */}
            </motion.div>
          </div>

          {/* Right Column - Empty for spacing */}
          <div className="col-span-12 lg:col-span-4 order-3 lg:order-3">
            {/* Content moved to absolute positioning below */}
          </div>
        </div>
        
        {/* Right side text - positioned at center-right */}
        <div className="absolute top-1/2 -translate-y-1/2 right-8 w-64 text-right hidden lg:block z-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.8 }}
          >
            <p 
              style={{
                fontFamily: 'Nanum Myeongjo',
                fontWeight: 700,
                fontSize: '17px',
                lineHeight: '26px',
                letterSpacing: '0%',
                color: '#620131'
              }}
            >
              High-precision mono-cartons for brands that demand perfection.
            </p>
          </motion.div>
        </div>
        
        {/* Large PrintAura Text at Bottom - ONLY ONE */}
        <div className="absolute -bottom-24 md:-bottom-32 left-1/2 -translate-x-1/2 w-full text-center pointer-events-none select-none overflow-hidden z-0">
          <h2 
            style={{
              fontFamily: 'Nanum Myeongjo',
              fontWeight: 800,
              fontSize: 'clamp(80px, 20vw, 303.41px)',
              lineHeight: '335.39px',
              letterSpacing: '0%',
              background: 'linear-gradient(180deg, #FF309D 34.49%, rgba(153, 29, 94, 0) 71.55%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              opacity: 0.3
            }}
          >
            PrintAura
          </h2>
        </div>
      </div>
    </motion.section>
  );
};
